import { AlertTriangle, Lock, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { InteractiveTimetableSlot } from '../TeacherTimetableSection';
import { REASON_BY_CATEGORY, REASON_LABEL } from './timetableCopy';

interface LockedNoticeModalProps {
  slot: InteractiveTimetableSlot;
  reason: string;
  onClose: () => void;
}

export function LockedNoticeModal({ slot, reason, onClose }: LockedNoticeModalProps) {
  // The status as stored, with the reason the teacher gave when they marked the
  // period unavailable — never "present" or "on leave", which the timetable
  // does not record.
  const recordedStatus =
    slot.status === 'AVAILABLE'
      ? 'Available'
      : slot.leaveCategory
        ? `Not available — ${REASON_LABEL[REASON_BY_CATEGORY[slot.leaveCategory]]}`
        : 'Not available';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fade-up">
      <div className="w-full max-w-md overflow-hidden rounded-[22px] bg-surface border border-line shadow-2xl flex flex-col text-xs">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-line bg-rose-500/10 px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-rose-500/20 text-rose-600 border border-rose-500/30">
              <Lock className="h-5 w-5 text-rose-500" />
            </span>
            <div>
              <h3 className="text-base font-bold text-ink">Period Record Locked</h3>
              <p className="text-xs text-rose-500 font-semibold">Read-Only Audit Mode</p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-ink-3 hover:bg-surface-3 hover:text-ink transition-colors cursor-pointer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 flex flex-col gap-3">
          <div className="p-3.5 rounded-xl bg-surface-2 border border-line flex flex-col gap-1">
            <span className="text-xs font-bold text-ink">
              {slot.day} ({slot.time}) — {slot.className} {slot.subject}
            </span>
            <span className="text-[11px] text-ink-3">
              Recorded Status:{' '}
              <strong
                className={
                  slot.status === 'AVAILABLE'
                    ? 'text-emerald-500 font-bold'
                    : 'text-rose-500 font-bold'
                }
              >
                {recordedStatus}
              </strong>
            </span>
          </div>

          <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-500 font-medium flex items-center gap-2">
            <AlertTriangle className="h-4.5 w-4.5 text-amber-500 shrink-0" />
            <span>{reason}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-line bg-surface-2 px-5 py-3.5 flex items-center justify-end">
          <Button variant="primary" size="sm" onClick={onClose} className="text-xs font-bold">
            Understand & Close
          </Button>
        </div>
      </div>
    </div>
  );
}
